"use client";

import type React from "react";
import { useState } from "react";

import { Calendar, HeartPulse, LogOut, Menu, Settings, X } from "lucide-react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { OrganizationSwitcher } from "@/components/organization-switcher";
import { ThemeToggle } from "@/components/theme/theme-toggle";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { UserRole } from "@/types/user-roles";

// Navigation items - only the main sections
const navigationItems = [
  {
    name: "Patients",
    href: "/patients",
    icon: HeartPulse,
    roles: [UserRole.ADMIN, UserRole.PROVIDER, UserRole.LYFE_ADMIN],
  },
  // {
  //   name: "Orders & Results",
  //   href: "/orders-results",
  //   icon: FileText,
  //   roles: [UserRole.ADMIN, UserRole.PROVIDER, UserRole.LYFE_ADMIN],
  // },
  {
    name: "Scheduling",
    href: "/scheduling",
    icon: Calendar,
    roles: [UserRole.ADMIN, UserRole.PROVIDER, UserRole.LYFE_ADMIN],
  },
  {
    name: "Admin",
    href: "/admin",
    icon: Settings,
    roles: [UserRole.ADMIN, UserRole.LYFE_ADMIN],
  },
];

interface CompactGlobalSidebarProps {
  user: {
    name?: string;
    email?: string;
    role?: UserRole;
    title?: string;
    department?: string;
  } | null;
  onSignOut: () => void;
}

export function CompactGlobalSidebar({ user, onSignOut }: CompactGlobalSidebarProps) {
  const pathname = usePathname();
  const [mobileOpen, setMobileOpen] = useState(false);

  // Filter navigation based on user role
  const filteredNavigation = user ? navigationItems.filter((item) => item.roles.includes(user.role as UserRole)) : [];

  const NavItem = ({ item, isMobile = false }: { item: (typeof navigationItems)[0]; isMobile?: boolean }) => {
    const isActive = pathname === item.href || pathname.startsWith(`${item.href}/`);

    if (isMobile) {
      return (
        <Link
          href={item.href}
          className={cn(
            "group/nav relative flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-150",
            isActive ? "bg-blue-50 text-blue-700" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          )}
          onClick={() => setMobileOpen(false)}
        >
          {isActive && (
            <span className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-blue-600" />
          )}
          <item.icon className="h-5 w-5 shrink-0" />
          {item.name}
        </Link>
      );
    }

    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            href={item.href}
            className={cn(
              "group/nav relative flex h-10 w-10 items-center justify-center rounded-lg transition-colors duration-150",
              isActive ? "bg-blue-50 text-blue-600" : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
            )}
          >
            {isActive && (
              <span className="absolute -left-[7px] top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-full bg-blue-600" />
            )}
            <item.icon className="h-5 w-5" />
            <span className="sr-only">{item.name}</span>
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right" sideOffset={10} className="font-medium">
          {item.name}
        </TooltipContent>
      </Tooltip>
    );
  };

  const UserAvatar = ({ size = "default" }: { size?: "default" | "large" }) => (
    <Avatar className={size === "large" ? "h-10 w-10" : "h-8 w-8"}>
      <AvatarImage src="/placeholder-avatar.png" alt={user?.name || "User"} />
      <AvatarFallback className="bg-blue-50 text-xs font-medium text-blue-700">
        {user?.name?.charAt(0)?.toUpperCase() || "U"}
      </AvatarFallback>
    </Avatar>
  );

  return (
    <TooltipProvider delayDuration={0}>
      {/* Desktop Sidebar - Compact Icon-Only */}
      <aside className="fixed left-0 top-0 z-40 hidden h-screen w-16 flex-col border-r border-slate-200 bg-white lg:flex">
        {/* Logo */}
        <div className="flex h-14 items-center justify-center border-b border-slate-200">
          <Link href="/dashboard" className="group/logo flex items-center justify-center">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 transition-colors duration-200 group-hover/logo:bg-blue-700">
              <span className="text-base font-semibold tracking-tight text-white">L</span>
            </div>
          </Link>
        </div>

        {/* Organization Switcher */}
        <div className="flex justify-center border-b border-slate-200 py-2">
          <OrganizationSwitcher variant="compact" showCreateOption={user?.role === UserRole.LYFE_ADMIN} />
        </div>

        {/* Navigation */}
        <nav className="flex flex-1 flex-col items-center gap-1 py-4">
          {filteredNavigation.map((item) => (
            <NavItem key={item.name} item={item} />
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className="flex flex-col items-center gap-2 border-t border-slate-200 py-4">
          <ThemeToggle />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-10 w-10">
                <UserAvatar />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent side="right" align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="flex flex-col space-y-1">
                  <p className="text-sm font-medium">{user?.name || "Unknown User"}</p>
                  <p className="text-xs text-muted-foreground">{user?.email || ""}</p>
                  <p className="text-xs capitalize text-muted-foreground">{user?.role?.toLowerCase() || "Provider"}</p>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onSignOut} className="text-red-600 focus:text-red-600">
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="fixed left-0 right-0 top-0 z-40 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 lg:hidden">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <Link href="/dashboard" className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <span className="text-sm font-semibold tracking-tight text-white">L</span>
            </div>
            <span className="font-semibold tracking-tight text-slate-900">LyfeAI</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <OrganizationSwitcher variant="compact" />
          <ThemeToggle />
          <UserAvatar />
        </div>
      </header>

      {/* Mobile Drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
          <div className="flex h-full flex-col">
            {/* Header */}
            <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
              <Link href="/dashboard" className="flex items-center gap-2" onClick={() => setMobileOpen(false)}>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                  <span className="text-sm font-semibold tracking-tight text-white">L</span>
                </div>
                <span className="font-semibold tracking-tight text-slate-900">LyfeAI</span>
              </Link>
              <Button variant="ghost" size="icon" onClick={() => setMobileOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Organization Switcher - Mobile */}
            <div className="border-b px-4 py-3">
              <OrganizationSwitcher variant="default" showCreateOption={user?.role === UserRole.LYFE_ADMIN} />
            </div>

            {/* User Info */}
            <div className="border-b p-4">
              <div className="flex items-center gap-3">
                <UserAvatar size="large" />
                <div className="flex-1 overflow-hidden">
                  <p className="truncate text-sm font-medium">{user?.name || "Unknown User"}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email || ""}</p>
                  <p className="truncate text-xs capitalize text-muted-foreground">
                    {user?.role?.toLowerCase() || "Provider"}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 space-y-1 overflow-y-auto p-4">
              {filteredNavigation.map((item) => (
                <NavItem key={item.name} item={item} isMobile />
              ))}
            </nav>

            {/* Footer */}
            <div className="border-t p-4">
              <Button variant="ghost" className="w-full justify-start text-red-600" onClick={onSignOut}>
                <LogOut className="mr-2 h-5 w-5" />
                Sign out
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </TooltipProvider>
  );
}

export default CompactGlobalSidebar;
